/**
 * v1.3.21 — Offline Field-Edit Sync Worker (Focus Mode Phase 3b).
 *
 * Drains the offline-field-edits queue when the app becomes
 * effectively online (network up AND Focus Mode off). Each queued
 * edit is replayed as a single `axios.patch(url, body)` in
 * INSERTION ORDER (FIFO) — so a user who edited an asset name,
 * then a description, then again the name in three separate saves
 * ends up with the server seeing exactly that sequence of PATCHes.
 *
 * DESIGN INVARIANTS (copy of deferredAiJobsSync.js contract)
 * ──────────────────────────────────────────────────────────
 *   1. Uses `subscribeConnectivity` from lib/connectivity.js — NOT
 *      raw browser events — so BOTH "network came back" AND
 *      "user turned off Focus Mode" transitions go through a
 *      single drain path.
 *   2. Toasts fire per-batch, not per-edit — a drain of 12 edits
 *      surfaces one aggregated success + one aggregated failure
 *      count.
 *   3. Edits that fail 5+ times are LEFT in the store in `failed`
 *      status for the Focus Mode drawer to surface (per-row Retry
 *      / Discard controls in the drawer UI).
 *   4. Only PATCH verb is supported (per Phase 3b scope). If a
 *      future release needs to queue POSTs/DELETEs, extend the
 *      record shape rather than special-casing here.
 */

import axios from 'axios';
import { toast } from 'sonner';

import {
  listPendingFieldEdits,
  updateFieldEditStatus,
  removeFieldEdit,
  publishFieldEditsChanged,
} from './offlineFieldEditQueue';
import { subscribeConnectivity, isEffectivelyOnline } from '../lib/connectivity';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_RETRIES = 5;

const _authHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : undefined;
};

/** Resolve a queued edit's endpoint to a full URL. Never trust the
 *  stored value — if the tenant's backend URL rotated between
 *  queue-time and drain-time, we want the CURRENT backend, not the
 *  one that was baked at queue-time. */
const _resolveUrl = (endpoint) => {
  if (!endpoint) return null;
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) return endpoint;
  if (endpoint.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${endpoint}`;
  if (endpoint.startsWith('/')) return `${API_BASE}${endpoint}`;
  return `${API_BASE}/${endpoint}`;
};

let isRunning = false;
let isInitialized = false;

const _runOne = async (edit) => {
  const url = _resolveUrl(edit.endpoint);
  if (!url) {
    await updateFieldEditStatus(edit.id, 'failed', 'missing endpoint');
    return { ok: false };
  }
  try {
    await updateFieldEditStatus(edit.id, 'running');
    const resp = await axios.patch(url, edit.body || {}, {
      timeout: 25000,
      headers: _authHeader(),
    });
    if (resp.status >= 200 && resp.status < 300) {
      await updateFieldEditStatus(edit.id, 'completed');
      // Prune completed rows so the store stays small — failures
      // stay behind for the drawer.
      await removeFieldEdit(edit.id);
      return { ok: true };
    }
    await updateFieldEditStatus(edit.id, 'failed', `HTTP ${resp.status}`);
    return { ok: false };
  } catch (err) {
    const msg = err?.response?.data?.detail || err?.message || 'unknown';
    await updateFieldEditStatus(edit.id, 'failed', msg);
    return { ok: false };
  }
};

/**
 * Public single-edit runner. Used by the drawer's per-row
 * "Run Now" button. Same code path as the batch drain — one at a
 * time. Bypasses the isEffectivelyOnline gate because a manual
 * "Run Now" is an explicit user override.
 */
export const runOneFieldEdit = _runOne;

/**
 * Drain every pending edit in FIFO order. Idempotent — safe to
 * call while a drain is already running (returns immediately).
 */
export const drainFieldEdits = async () => {
  if (isRunning) return;
  if (!isEffectivelyOnline()) return;
  isRunning = true;
  try {
    const pending = await listPendingFieldEdits();
    if (pending.length === 0) return;

    let succeeded = 0;
    let failed = 0;
    for (const edit of pending) {
      if ((edit.retry_count || 0) >= MAX_RETRIES) continue;
      // Mid-drain toggle-off guard: don't burn through the queue
      // if the user just flipped Focus Mode back on.
      if (!isEffectivelyOnline()) break;
      // eslint-disable-next-line no-await-in-loop
      const res = await _runOne(edit);
      if (res.ok) succeeded += 1;
      else failed += 1;
    }

    if (succeeded > 0) {
      toast.success(
        `Synced ${succeeded} queued edit${succeeded > 1 ? 's' : ''}`,
        { duration: 4500 },
      );
    }
    if (failed > 0) {
      toast.warning(
        `${failed} queued edit${failed > 1 ? 's' : ''} failed — check the Focus Mode drawer`,
        { duration: 5500 },
      );
    }
    publishFieldEditsChanged();
  } finally {
    isRunning = false;
  }
};

/**
 * One-time init. Subscribes to connectivity so a network up OR a
 * Focus Mode off transition triggers a drain. Also fires an initial
 * drain on boot if the app started effectively-online (covers the
 * case where the user closed the tab with unsynced edits pending).
 */
export const initOfflineFieldEditSync = () => {
  if (isInitialized) return;
  isInitialized = true;

  subscribeConnectivity(({ effectiveOnline }) => {
    if (effectiveOnline) {
      // Small grace window so a network flap doesn't cause a stampede.
      setTimeout(() => { drainFieldEdits(); }, 900);
    }
  });

  if (isEffectivelyOnline()) {
    // Slight delay so the auth token is definitely present in
    // localStorage before we start PATCHing.
    setTimeout(() => { drainFieldEdits(); }, 2500);
  }
};
